// ERA Motor — Push Notification helpers (Aşama 2 v2.0)
import api from "./api";

const SW_PATH = "/sw.js";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isPWAStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export async function registerSW() {
  if (!pushSupported()) return null;
  try {
    let reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!reg) reg = await navigator.serviceWorker.register(SW_PATH, { updateViaCache: "none" });
    // Manuel update tetikle — index.html cache'lenmiş olsa bile sw.js taze indirilir
    try { await reg.update(); } catch (_e) { /* ignore */ }
    await navigator.serviceWorker.ready;
    // Yeni SW aktifleştiğinde (controllerchange) sayfayı bir kez yenile —
    // eski bundle ile eşleşmezliği önler. YALNIZCA halihazırda bir controller
    // varsa reload et (ilk kurulumda gereksiz reload olmasın).
    if (!window.__eraSwCtrlListener) {
      window.__eraSwCtrlListener = true;
      const hadController = !!navigator.serviceWorker.controller;
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!hadController) return; // İlk kurulum — reload YOK
        if (refreshing) return;
        refreshing = true;
        try { window.location.reload(); } catch (_e) { /* ignore */ }
      });
    }
    return reg;
  } catch (e) {
    console.error("[SW] register failed", e);
    return null;
  }
}

/** Tur 3 — Yeni bir SW sürümü hazır olduğunda callback tetikler.
 *  Aktif SW pas geçilmez; kullanıcı onaylayınca SKIP_WAITING mesajı gönderilir.  */
export function watchForSWUpdate(onUpdateReady) {
  if (typeof onUpdateReady !== "function") return () => {};
  if (!pushSupported()) return () => {};
  let cancelled = false;
  navigator.serviceWorker.getRegistration(SW_PATH).then((reg) => {
    if (!reg || cancelled) return;
    const notifyIfWaiting = () => { if (reg.waiting) onUpdateReady(reg); };
    notifyIfWaiting();
    reg.addEventListener("updatefound", () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        if (nw.state === "installed" && navigator.serviceWorker.controller) {
          onUpdateReady(reg);
        }
      });
    });
    // Periyodik güncelleme sorgusu (30dk)
    setInterval(() => {
      try { reg.update(); } catch (e) { /* ignore */ }
    }, 30 * 60 * 1000);
  });
  return () => { cancelled = true; };
}

/** Kullanıcı onayı ile yeni SW'yi aktifleştir ve sayfayı yenile. */
export async function activatePendingSW() {
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg || !reg.waiting) return false;
  reg.waiting.postMessage({ type: "SKIP_WAITING" });
  await new Promise((resolve) => {
    const onCtrl = () => { navigator.serviceWorker.removeEventListener("controllerchange", onCtrl); resolve(); };
    navigator.serviceWorker.addEventListener("controllerchange", onCtrl);
    setTimeout(onCtrl, 3000);
  });
  window.location.reload();
  return true;
}

export async function getNotificationStatus() {
  if (!pushSupported()) return "unsupported";
  const perm = Notification.permission;
  if (perm === "denied") return "denied";
  if (perm === "default") return "prompt";
  // granted → check subscription
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return "no_sw";
  const sub = await reg.pushManager.getSubscription();
  return sub ? "active" : "granted_no_sub";
}

export async function enableNotifications() {
  if (!pushSupported()) throw new Error("Bu cihaz push bildirimini desteklemiyor");
  // iOS PWA gereksinim uyarısı — cihaz standalone değilse ve iOS ise
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS && !isPWAStandalone()) {
    throw new Error("iOS'te bildirim için önce PWA olarak Ana Ekrana ekleyip uygulamayı ana ekrandan açın.");
  }

  const reg = await registerSW();
  if (!reg) throw new Error("Service Worker kaydı başarısız");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Bildirim izni reddedildi");

  // VAPID public key al
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);

  // Mevcut subscription varsa yeniden kullan, yoksa oluştur
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  const payload = sub.toJSON();
  // Backend'e kaydet
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    device_info: `${ua.slice(0, 100)} | standalone=${isPWAStandalone()}`,
  });
  return { endpoint: payload.endpoint };
}

export async function disableNotifications() {
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch (e) { /* ignore */ }
    await sub.unsubscribe();
  }
}

export async function sendTestNotification() {
  const { data } = await api.post("/push/test");
  return data;
}
